import { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Trash } from "@phosphor-icons/react";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";

const MEASURE_TEMPLATE = [
  ["evv", "EVV Compliance Rate"],
  ["billable", "Total Billable Hours"],
  ["visits", "Completed Visits & Missed Visits"],
  ["incidents", "Incidents (near miss / critical / APS / preventable)"],
  ["interventions", "Interventions Applied — effectiveness"],
  ["complaints", "Complaints (resolved to participant satisfaction)"],
  ["task_entry", "Task Code Entry Rate"],
  ["task_compliance", "Task Code Compliance Rate"],
  ["overnight", "Overnight Check-in Compliance Rate"],
];

const POLICY_TEMPLATE = [
  "Personal Care Services", "Sexual Harassment", "Limited English Proficiency (LEP)",
  "Records Management", "Dress Code", "Social Security Verification",
  "OIG Inspection Lists", "Securing Electronic Records and Documents",
];

const STATUS_OPTS = ["Improved", "Remained Constant", "Declined"];
const POLICY_OPTS = ["Reviewed", "Updated", "No Changes"];
const DEFAULT_ATTENDEES = "Kelly Schmunk, Patricia Carvalho, Bruce Carvalho";

const emptyMeasure = ([key, label]) => ({ key, label, target: "", prev: "", current: "", status: "", qmp: "", deadline: "", leadership: "", note: "" });

const MSTATUS_CLS = {
  Improved: "text-[#1e8449]",
  Declined: "text-[#C0392B]",
  "Remained Constant": "text-stone-500",
};

export function CommitteeMeetingForm({ onSaved, onCancel }) {
  const now = new Date();
  const [header, setHeader] = useState({
    year: String(now.getFullYear()),
    quarter: `Q${Math.floor(now.getMonth() / 3) + 1}`,
    meeting_date: now.toISOString().slice(0, 10),
    attendees: DEFAULT_ATTENDEES,
  });
  const [measures, setMeasures] = useState(MEASURE_TEMPLATE.map(emptyMeasure));
  const [policies, setPolicies] = useState(POLICY_TEMPLATE.map((name) => ({ name, state: "" })));
  const [text, setText] = useState({ compliance_notes: "", prev_actions: "", other_topics: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/policies").then((r) => {
      const pool = (r.data || []).map((p) => p.title).filter(Boolean)
        .filter((t) => !POLICY_TEMPLATE.includes(t));
      let seed = 0;
      for (const ch of `${header.year}-${header.quarter}`) seed = (seed * 31 + ch.charCodeAt(0)) >>> 0;
      const picked = [];
      while (picked.length < 3 && pool.length) {
        seed = (seed * 1103515245 + 12345) >>> 0;
        picked.push(pool.splice(seed % pool.length, 1)[0]);
      }
      setPolicies([...POLICY_TEMPLATE.map((name) => ({ name, state: "" })),
                   ...picked.map((name) => ({ name, state: "", rotating: true }))]);
    }).catch(() => {});
  }, [header.year, header.quarter]);

  useEffect(() => {
    const q = `${header.year}-${header.quarter}`;
    if (!/^\d{4}-Q[1-4]$/.test(q)) return;
    api.get(`/qi/quarter-stats?quarter=${q}`).then((r) => {
      const s = r.data;
      setMeasures((ms) => ms.map((m) => {
        if (m.key === "incidents" && !m.current) return { ...m, current: String(s.incidents) };
        if (m.key === "complaints" && !m.current) return { ...m, current: String(s.complaints) };
        return m;
      }));
    }).catch(() => {});
  }, [header.year, header.quarter]);

  const setM = (key, field, val) => setMeasures((ms) => ms.map((m) => (m.key === key ? { ...m, [field]: val } : m)));
  const setP = (name, val) => setPolicies((ps) => ps.map((p) => (p.name === name ? { ...p, state: val } : p)));

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/qi/entries", {
        kind: "committee_note",
        title: `${header.year} ${header.quarter} Quality Management Meeting`,
        date: header.meeting_date,
        quarter: `${header.year}-${header.quarter}`,
        meeting_date: header.meeting_date,
        attendees: header.attendees,
        compliance_notes: text.compliance_notes,
        prev_actions: text.prev_actions,
        other_topics: text.other_topics,
        measures,
        policy_reviews: policies,
      });
      toast.success("Meeting record saved");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  const cellCls = "h-8 w-full min-w-0 rounded-md border border-stone-300 bg-white px-1.5 text-xs text-stone-700";

  return (
    <div className="rounded-lg border border-stone-200 bg-white p-5 shadow-sm" data-testid="qi-meeting-form">
      <div className="grid gap-3 sm:grid-cols-4">
        <div>
          <label className="mb-1 block text-xs font-medium text-stone-500">Quarter</label>
          <select value={header.quarter} onChange={(e) => setHeader((h) => ({ ...h, quarter: e.target.value }))}
            className="h-9 w-full rounded-md border border-stone-300 bg-white px-2 text-sm text-stone-700" data-testid="qi-meeting-quarter">
            {["Q1", "Q2", "Q3", "Q4"].map((q) => <option key={q} value={q}>{q}</option>)}
          </select>
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-stone-500">Year</label>
          <Input value={header.year} onChange={(e) => setHeader((h) => ({ ...h, year: e.target.value }))} className="h-9 border-stone-300 bg-white text-sm" data-testid="qi-meeting-year" />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-stone-500">Meeting date</label>
          <Input type="date" value={header.meeting_date} onChange={(e) => setHeader((h) => ({ ...h, meeting_date: e.target.value }))} className="h-9 border-stone-300 bg-white text-sm" data-testid="qi-meeting-date" />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-stone-500">Participants</label>
          <Input value={header.attendees} onChange={(e) => setHeader((h) => ({ ...h, attendees: e.target.value }))} className="h-9 border-stone-300 bg-white text-sm" data-testid="qi-meeting-attendees" />
        </div>
      </div>

      <h3 className="mt-5 font-heading text-base font-semibold text-stone-900">Performance Measures</h3>
      <p className="mt-0.5 text-xs text-stone-400">Incident and complaint counts for the quarter are pre-filled from logged records.</p>
      <div className="mt-2 overflow-x-auto">
        <table className="w-full min-w-[900px] text-left text-xs">
          <thead>
            <tr className="border-b border-stone-200 text-[10px] uppercase tracking-wide text-stone-400">
              <th className="py-2 pr-2 font-medium">Measure</th>
              <th className="py-2 pr-2 font-medium">Target</th>
              <th className="py-2 pr-2 font-medium">Prev Qtr</th>
              <th className="py-2 pr-2 font-medium">Current Qtr</th>
              <th className="py-2 pr-2 font-medium">Status</th>
              <th className="py-2 pr-2 font-medium">QMP?</th>
              <th className="py-2 pr-2 font-medium">Deadline</th>
              <th className="py-2 pr-2 font-medium">Leadership</th>
              <th className="py-2 font-medium">Note</th>
            </tr>
          </thead>
          <tbody>
            {measures.map((m) => (
              <tr key={m.key} className="border-b border-stone-100" data-testid={`qi-measure-${m.key}`}>
                <td className="py-1.5 pr-2 text-stone-700">{m.label}</td>
                <td className="py-1.5 pr-2"><input value={m.target} onChange={(e) => setM(m.key, "target", e.target.value)} className={cellCls} data-testid={`qi-m-${m.key}-target`} /></td>
                <td className="py-1.5 pr-2"><input value={m.prev} onChange={(e) => setM(m.key, "prev", e.target.value)} className={cellCls} data-testid={`qi-m-${m.key}-prev`} /></td>
                <td className="py-1.5 pr-2"><input value={m.current} onChange={(e) => setM(m.key, "current", e.target.value)} className={cellCls} data-testid={`qi-m-${m.key}-current`} /></td>
                <td className="py-1.5 pr-2">
                  <select value={m.status} onChange={(e) => setM(m.key, "status", e.target.value)} className={cellCls} data-testid={`qi-m-${m.key}-status`}>
                    <option value="">—</option>
                    {STATUS_OPTS.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </td>
                <td className="py-1.5 pr-2">
                  <select value={m.qmp} onChange={(e) => setM(m.key, "qmp", e.target.value)} className={cellCls} data-testid={`qi-m-${m.key}-qmp`}>
                    <option value="">—</option>
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                  </select>
                </td>
                <td className="py-1.5 pr-2"><input type="date" value={m.deadline} onChange={(e) => setM(m.key, "deadline", e.target.value)} className={cellCls} data-testid={`qi-m-${m.key}-deadline`} /></td>
                <td className="py-1.5 pr-2"><input value={m.leadership} onChange={(e) => setM(m.key, "leadership", e.target.value)} className={cellCls} data-testid={`qi-m-${m.key}-leadership`} /></td>
                <td className="py-1.5"><input value={m.note} onChange={(e) => setM(m.key, "note", e.target.value)} className={cellCls} data-testid={`qi-m-${m.key}-note`} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-4 grid gap-3 sm:grid-cols-3">
        <div>
          <label className="mb-1 block text-xs font-medium text-stone-500">Weekly compliance report review — non-compliance issues</label>
          <Textarea value={text.compliance_notes} onChange={(e) => setText((t) => ({ ...t, compliance_notes: e.target.value }))} className="border-stone-300 bg-white text-sm" data-testid="qi-meeting-compliance" />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-stone-500">Previous meeting actions / outcomes</label>
          <Textarea value={text.prev_actions} onChange={(e) => setText((t) => ({ ...t, prev_actions: e.target.value }))} className="border-stone-300 bg-white text-sm" data-testid="qi-meeting-prev-actions" />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-stone-500">Other notable topics or projects</label>
          <Textarea value={text.other_topics} onChange={(e) => setText((t) => ({ ...t, other_topics: e.target.value }))} className="border-stone-300 bg-white text-sm" data-testid="qi-meeting-other" />
        </div>
      </div>

      <h3 className="mt-5 font-heading text-base font-semibold text-stone-900">Policies & Procedures Review</h3>
      <p className="mt-0.5 text-xs text-stone-400">Three policies rotate in each quarter (highlighted) so the whole library gets reviewed over time.</p>
      <div className="mt-2 grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
        {policies.map((p) => (
          <div key={p.name} className={`flex items-center justify-between gap-2 rounded-md border px-2.5 py-1.5 ${p.rotating ? "border-primary/50 bg-primary/5" : "border-stone-200"}`} data-testid={`qi-policy-${p.name.replace(/[^A-Za-z0-9]+/g, "-").toLowerCase()}`}>
            <span className="text-xs text-stone-700">{p.name}
              {p.rotating && <span className="ml-1 rounded-full bg-primary/10 px-1.5 py-0.5 text-[9px] font-medium text-primary" data-testid={`qi-policy-rotating-${p.name.replace(/[^A-Za-z0-9]+/g, "-").toLowerCase()}`}>rotating</span>}
            </span>
            <select value={p.state} onChange={(e) => setP(p.name, e.target.value)}
              className="h-7 rounded-md border border-stone-300 bg-white px-1 text-xs text-stone-700" data-testid={`qi-policy-select-${p.name.replace(/[^A-Za-z0-9]+/g, "-").toLowerCase()}`}>
              <option value="">—</option>
              {POLICY_OPTS.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        ))}
      </div>

      <div className="mt-5 flex gap-2">
        <Button onClick={submit} disabled={busy} className="bg-primary text-primary-foreground hover:bg-stone-900" data-testid="qi-meeting-save">
          {busy ? "Saving…" : "Save meeting record"}
        </Button>
        <Button onClick={onCancel} variant="outline" className="border-stone-300 text-stone-700" data-testid="qi-meeting-cancel">Cancel</Button>
      </div>
    </div>
  );
}

export function CommitteeMeetingCard({ e, onDelete }) {
  const filled = (e.measures || []).filter((m) => m.target || m.prev || m.current || m.status);
  const reviewedPolicies = (e.policy_reviews || []).filter((p) => p.state);
  return (
    <div className="rounded-lg border border-stone-200 bg-white p-5 shadow-sm" data-testid={`qi-entry-${e.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <p className="font-heading text-base font-semibold text-stone-900">{e.title}</p>
          <p className="mt-0.5 text-xs text-stone-400">{e.meeting_date || e.date || ""}{e.attendees ? ` · ${e.attendees}` : ""}</p>
        </div>
        <button onClick={() => onDelete(e)} className="text-stone-300 hover:text-[#C0392B]" data-testid={`qi-delete-${e.id}`} title="Delete">
          <Trash size={14} />
        </button>
      </div>
      {filled.length > 0 && (
        <div className="mt-3 overflow-x-auto">
          <table className="w-full min-w-[700px] text-left text-xs">
            <thead>
              <tr className="border-b border-stone-200 text-[10px] uppercase tracking-wide text-stone-400">
                <th className="py-1.5 pr-2 font-medium">Measure</th>
                <th className="py-1.5 pr-2 font-medium">Target</th>
                <th className="py-1.5 pr-2 font-medium">Prev</th>
                <th className="py-1.5 pr-2 font-medium">Current</th>
                <th className="py-1.5 pr-2 font-medium">Status</th>
                <th className="py-1.5 pr-2 font-medium">QMP</th>
                <th className="py-1.5 font-medium">Leadership / note</th>
              </tr>
            </thead>
            <tbody>
              {filled.map((m) => (
                <tr key={m.key} className="border-b border-stone-100" data-testid={`qi-measure-row-${e.id}-${m.key}`}>
                  <td className="py-1.5 pr-2 text-stone-700">{m.label}</td>
                  <td className="py-1.5 pr-2 text-stone-600">{m.target || "—"}</td>
                  <td className="py-1.5 pr-2 text-stone-600">{m.prev || "—"}</td>
                  <td className="py-1.5 pr-2 font-medium text-stone-900">{m.current || "—"}</td>
                  <td className={`py-1.5 pr-2 font-medium ${MSTATUS_CLS[m.status] || "text-stone-400"}`}>{m.status || "—"}</td>
                  <td className={`py-1.5 pr-2 font-medium ${m.qmp === "Yes" ? "text-[#C0392B]" : "text-stone-400"}`}>{m.qmp || "—"}</td>
                  <td className="py-1.5 text-stone-600">{[m.leadership, m.note].filter(Boolean).join(" — ") || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {e.compliance_notes && <p className="mt-3 text-sm text-stone-600"><span className="font-medium text-stone-700">Compliance review:</span> {e.compliance_notes}</p>}
      {e.prev_actions && <p className="mt-1.5 text-sm text-stone-600"><span className="font-medium text-stone-700">Previous actions/outcomes:</span> {e.prev_actions}</p>}
      {e.other_topics && <p className="mt-1.5 text-sm text-stone-600"><span className="font-medium text-stone-700">Other topics:</span> {e.other_topics}</p>}
      {reviewedPolicies.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {reviewedPolicies.map((p) => (
            <span key={p.name} className={`rounded-full border px-2 py-0.5 text-[10px] font-medium ${p.state === "Updated" ? "border-[#B7791F]/30 bg-[#B7791F]/10 text-[#8a5d14]" : "border-stone-300 bg-stone-100 text-stone-500"}`}
              data-testid={`qi-policy-chip-${e.id}-${p.name.replace(/[^A-Za-z0-9]+/g, "-").toLowerCase()}`}>
              {p.name}: {p.state}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
